import * as React from 'react';
import { Link } from 'react-router-dom';
import { AuthDescription } from '../AuthDescription';
import { AuthForm } from '../AuthForm';
import { AuthFormError } from '../AuthFormError';
import { AuthFormRow } from '../AuthFormRow';
import { AuthHeading } from '../AuthHeading';
import {
  AuthRedirectMessage,
  AuthRedirectMessageType
} from '../AuthRedirectMessage';
import { Button } from '../../Forms/Button';
import { Checkbox } from '../../Forms/Checkbox';
import { FormRow } from '../../Forms/FormRow';
import './style.css';

export interface LoginProps {
  error: boolean;
  loading: boolean;
  onLogin: (username: string, password: string, rememberMe: boolean) => void;
}

export interface LoginState {
  username: string;
  usernameError?: string;
  password: string;
  passwordError?: string;
  rememberMe: boolean;
}

export class Login extends React.Component<LoginProps, LoginState> {
  constructor(props: LoginProps) {
    super(props);

    this.state = {
      username: '',
      password: '',
      rememberMe: false
    };
  }

  render() {
    const { error, loading } = this.props;
    const {
      username,
      usernameError,
      password,
      passwordError,
      rememberMe
    } = this.state;

    return (
      <main className="login">
        <AuthHeading>Log into your Edge of Play account</AuthHeading>
        <AuthDescription>
          Bringing cutting edge coaching material for the sports you love right
          to your fingertips.
          <br />
          Log in now to keep track of your favourite content and get in touch
          with the community.
        </AuthDescription>
        <AuthForm onSubmit={this.tryLogin}>
          <AuthFormRow
            className="login-form-row"
            inputClassName="login-input"
            label="Your Email:"
            autoComplete="email"
            autoFocus={true}
            value={username}
            error={usernameError}
            onChange={event =>
              this.updateUsername((event.target as HTMLInputElement).value)
            }
          />
          <AuthFormRow
            className="login-form-row"
            inputClassName="login-input"
            label="Password:"
            autoComplete="current-password"
            type="password"
            value={password}
            error={passwordError}
            onChange={event =>
              this.updatePassword((event.target as HTMLInputElement).value)
            }
          />
          <AuthFormError
            className="edge-of-play-form-row login-error"
            error={
              error
                ? `Login failed, please check your credentials and try again`
                : undefined
            }
          />
          <FormRow className="forgot-password-form-row">
            <label className="login-remember-me-label">
              <Checkbox
                className="login-remember-me-checkbox"
                checked={rememberMe}
                onChange={event =>
                  this.setState({ rememberMe: event.currentTarget.checked })
                }
              />
              Keep me logged in
            </label>
            <Link
              className="edge-of-play-link forgot-password-link"
              to="/forgot-password"
            >
              Forgot your password?
            </Link>
          </FormRow>
          <FormRow>
            <Button className="login-button" type="submit" disabled={loading}>
              Log In
            </Button>
          </FormRow>
          <FormRow>
            <AuthRedirectMessage
              type={AuthRedirectMessageType.RedirectToSignUp}
            />
          </FormRow>
        </AuthForm>
      </main>
    );
  }

  private shouldSubmitBeDisabled = (): boolean => {
    const { loading } = this.props;
    const { username, usernameError, password, passwordError } = this.state;

    return !!(
      loading ||
      !username ||
      !password ||
      usernameError ||
      passwordError
    );
  };

  private updateUsername = (username: string): void => {
    this.validateUsername(username);
    this.setState({
      username
    });
  };

  private updatePassword = (password: string): void => {
    this.validatePassword(password);
    this.setState({
      password
    });
  };

  private validateUsername = (username: string): void => {
    this.setState({
      usernameError: this.getUsernameError(username)
    });
  };

  private validatePassword = (password: string): void => {
    this.setState({
      passwordError: this.getPasswordError(password)
    });
  };

  private tryLogin = (event: React.FormEvent<HTMLFormElement>) => {
    const { onLogin } = this.props;
    const { password, rememberMe, username } = this.state;

    event.preventDefault();

    // Force validation before we check if we can submit
    this.validateUsername(username);
    this.validatePassword(password);

    if (onLogin && !this.shouldSubmitBeDisabled()) {
      onLogin(username as string, password as string, rememberMe);
    }
  };

  private getUsernameError = (username?: string): string | undefined => {
    return username && username.length
      ? undefined
      : `Username must be supplied`;
  };

  private getPasswordError = (password?: string): string | undefined => {
    return password && password.length
      ? undefined
      : `Password must be supplied`;
  };
}



// WEBPACK FOOTER //
// ./src/app/components/Auth/Login/index.tsx